import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  AlertColor,
  CircularProgress,
  MenuItem,
  Snackbar,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  InputAdornment,
  Tooltip,
  Avatar,
  Autocomplete,
  Checkbox,
  Paper,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridActionsCellItem
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Visibility,
  VisibilityOff,
  Security as SecurityIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { apiUrl } from '../utils/api';
import StyledDataGrid from '../components/StyledDataGrid';
import { validatePassword } from '../utils/passwordValidator';
import PasswordPolicyChecklist from '../components/PasswordPolicyChecklist';

interface User {
  user_id: number;
  user_first_name: string;
  user_last_name: string;
  user_email: string;
  primary_company_id: number | null;
  is_active: boolean;
  highest_role: string;
  access_groups?: any[];
  accessible_companies?: any[];
}

interface Company {
  company_id: number;
  company_name: string;
}

interface Project {
  project_id: number;
  project_name: string;
  company_id: number;
}

interface UsersProps {
  companyId?: number | null;
  isGlobalAdmin?: boolean;
  currentUser?: any;
  access?: string;
}

const Users: React.FC<UsersProps> = ({
  companyId = null,
  isGlobalAdmin = false,
  currentUser = null,
  access = 'none'
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [openAccessDialog, setOpenAccessDialog] = useState(false);
  const [managingUser, setManagingUser] = useState<User | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [updatingStatusId, setUpdatingStatusId] = useState<number | null>(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    open: boolean;
    user: User | null;
  }>({
    open: false,
    user: null
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'all'>('active');
  const [showPassword, setShowPassword] = useState(false);
  const [page, setPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [formData, setFormData] = useState({
    user_first_name: '',
    user_last_name: '',
    user_email: '',
    password: '',
    group_ids: [] as number[]
  });

  const [accessFormData, setAccessFormData] = useState({
    group_ids: [] as number[]
  });
  const [groupSearchTerm, setGroupSearchTerm] = useState('');


  const showNotification = (message: string, severity: AlertColor = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const loadAllData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Run all fetches in parallel
      await Promise.all([
        fetchUsers(),
        fetchCompanies(),
        fetchProjects(),
        fetchGroups()
      ]);
    } catch (err: any) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, page, searchTerm, perPage, statusFilter]);

  useEffect(() => {
    if (access !== 'none') {
      loadAllData();
    }
  }, [access, loadAllData]);

  const fetchUsers = async () => {
    try {
      let url = apiUrl('/api/users');
      const params = new URLSearchParams();

      // Add pagination parameters
      params.append('page', page.toString());
      params.append('per_page', perPage.toString());
      params.append('status', statusFilter);

      // Always filter by selecteFad company (even for admins viewing specific company)
      if (companyId) {
        params.append('company_id', companyId.toString());
      }

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      if (params.toString()) {
        url += '?' + params.toString();
      }

      const response = await fetch(url, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);

        // Handle pagination info from backend
        if (data.pagination) {
          setTotalUsers(data.pagination.total);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load users');
      }
    } catch (err: any) {
      setError(err.message);
      showNotification(err.message, 'error');
      throw err; // Re-throw to let loadAllData handle it
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await fetch(apiUrl('/api/companies'), {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
      }
    } catch (err) {
      console.error('Failed to load companies:', err);
    }
  };

  const fetchProjects = async () => {
    try {
      if (!currentUser || !currentUser.user_id) return;

      const response = await fetch(apiUrl(`/api/users/${currentUser.user_id}/projects`), {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      }
    } catch (err) {
      console.error('Failed to load projects:', err);
    }
  };

  const fetchGroups = async () => {
    try {
      console.log('[Users] Fetching groups...');
      console.log('[Users] isGlobalAdmin:', isGlobalAdmin);
      console.log('[Users] currentUser:', currentUser);

      const response = await fetch(apiUrl('/api/groups'), {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        console.log('[Users] Groups loaded:', data);
        const allGroups = Array.isArray(data) ? data : [];

        // Filter groups based on current user's level
        let filteredGroups = allGroups;

        // Only filter if NOT Global Admin
        if (!isGlobalAdmin && currentUser?.access_groups && currentUser.access_groups.length > 0) {
          console.log('[Users] Applying filtering for non-Global Admin user');

          // Get current user's companies from their groups
          const userCompanyIds = currentUser.access_groups
            .filter((g: any) => g.company_id)
            .map((g: any) => g.company_id);

          console.log('[Users] User company IDs:', userCompanyIds);

          // Check if user is Company Admin (has groups with company_id but no project_ids)
          const isCompanyAdmin = currentUser.access_groups.some(
            (g: any) => g.company_id && (!g.project_ids || g.project_ids.length === 0)
          );

          console.log('[Users] Is Company Admin:', isCompanyAdmin);

          if (isCompanyAdmin && userCompanyIds.length > 0) {
            // Company Admin: Show ALL groups from their company
            filteredGroups = allGroups.filter((g: any) =>
              userCompanyIds.includes(g.company_id)
            );
            console.log('[Users] Company Admin - Filtered to company groups:', filteredGroups.length);
          } else {
            // Project level user: Show only groups from their projects
            const userProjectIds = currentUser.access_groups
              .flatMap((g: any) => g.project_ids || []);

            console.log('[Users] User project IDs:', userProjectIds);

            if (userProjectIds.length > 0) {
              filteredGroups = allGroups.filter((g: any) => {
                if (!g.project_ids || g.project_ids.length === 0) return false;
                return g.project_ids.some((pid: number) => userProjectIds.includes(pid));
              });
              console.log('[Users] Project level - Filtered to project groups:', filteredGroups.length);
            } else {
              console.log('[Users] No project IDs found, showing all groups');
            }
          }
        } else {
          console.log('[Users] No filtering applied - showing all groups');
        }

        setGroups(filteredGroups);
      } else {
        console.error('[Users] Failed to load groups - Status:', response.status);
      }
    } catch (err) {
      console.error('[Users] Failed to load groups:', err);
    }
  };

  const handleOpenDialog = (user: User | null = null) => {
    console.log('[Users] Opening dialog. Available groups:', groups.length, groups);
    if (user) {
      setEditingUser(user);
      setFormData({
        user_first_name: user.user_first_name || '',
        user_last_name: user.user_last_name || '',
        user_email: user.user_email,
        password: '',
        group_ids: []
      });
    } else {
      setEditingUser(null);
      setFormData({
        user_first_name: '',
        user_last_name: '',
        user_email: '',
        password: '',
        group_ids: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setError(null);
  };

  const handleOpenAccessDialog = async (user: User) => {
    setManagingUser(user);

    // Open dialog immediately with user's current groups
    const userGroupIds = (user.access_groups || []).map((g: any) => g.group_id);
    setAccessFormData({
      group_ids: userGroupIds
    });
    setOpenAccessDialog(true);

    // Fetch detailed user data in background to refresh group assignments
    try {
      const response = await fetch(apiUrl(`/api/users/${user.user_id}`), {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const userData = data.user || data;

        // Get user's current group IDs
        const currentGroupIds = (userData.access_groups || []).map((g: any) => g.group_id);

        // Update form with latest data
        setAccessFormData({
          group_ids: currentGroupIds
        });
      }
    } catch (err) {
      console.error('Failed to fetch user details:', err);
    }
  };

  const handleCloseAccessDialog = () => {
    setOpenAccessDialog(false);
    setManagingUser(null);
    setError(null);
    setGroupSearchTerm('');
  };

  const handleSaveAccess = async () => {
    if (!managingUser) return;

    try {
      setSaving(true);
      setError(null);

      const response = await fetch(apiUrl(`/api/users/${managingUser.user_id}/access`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(accessFormData)
      });

      if (response.ok) {
        await fetchUsers();
        handleCloseAccessDialog();
        showNotification('User access updated successfully!', 'success');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update user access');
      }
    } catch (err: any) {
      setError(err.message);
      showNotification(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Trim whitespace from string fields
      const trimmedData = {
        user_first_name: formData.user_first_name?.trim() || '',
        user_last_name: formData.user_last_name?.trim() || '',
        user_email: formData.user_email?.trim().toLowerCase() || '',
        password: formData.password
      };

      // Validation
      if (!trimmedData.user_email) {
        throw new Error('Email is required');
      }

      // Email format validation
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailPattern.test(trimmedData.user_email)) {
        throw new Error('Invalid email format');
      }

      // First name validation - only letters, spaces, hyphens, and apostrophes
      if (trimmedData.user_first_name) {
        const namePattern = /^[a-zA-Z\s\-']+$/;
        if (!namePattern.test(trimmedData.user_first_name)) {
          throw new Error('First name can only contain letters, spaces, hyphens, and apostrophes');
        }
      }

      // Last name validation - only letters, spaces, hyphens, and apostrophes
      if (trimmedData.user_last_name) {
        const namePattern = /^[a-zA-Z\s\-']+$/;
        if (!namePattern.test(trimmedData.user_last_name)) {
          throw new Error('Last name can only contain letters, spaces, hyphens, and apostrophes');
        }
      }

      // New users set their own password via the emailed setup link, so no
      // password is collected here at creation time.

      // Enforce the password policy whenever a password IS being submitted —
      // e.g. an admin setting a new password while editing an existing user.
      if (trimmedData.password) {
        const { isValid } = validatePassword(trimmedData.password);
        if (!isValid) {
          throw new Error(
            'Password does not meet the policy. Please review the requirements below the password field.'
          );
        }
      }

      // CLIENT REQUIREMENT: At least one group is MANDATORY for new users
      if (!editingUser && (!formData.group_ids || formData.group_ids.length === 0)) {
        throw new Error('At least one access group is required. Please select a group.');
      }

      const url = editingUser
        ? apiUrl(`/api/users/${editingUser.user_id}`)
        : apiUrl('/api/users');

      const payload: any = {
        user_first_name: trimmedData.user_first_name,
        user_last_name: trimmedData.user_last_name,
        user_email: trimmedData.user_email
      };

      // Only include password if provided
      if (formData.password) {
        payload.password = formData.password;
      }

      // Include group_ids only for new users (CLIENT REQUIREMENT: Group-based access control)
      if (!editingUser) {
        payload.group_ids = formData.group_ids || [];
      }

      const response = await fetch(url, {
        method: editingUser ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const respData = await response.json().catch(() => ({}));
        await fetchUsers();
        handleCloseDialog();
        if (editingUser) {
          showNotification('User updated successfully!', 'success');
        } else if (respData.welcome_email_sent) {
          showNotification(
            'User created! A setup link has been emailed to them.',
            'success'
          );
        } else {
          // User was created, but the welcome email did not go out.
          showNotification(
            'User created, but the setup email could not be sent' +
              (respData.welcome_email_error ? `: ${respData.welcome_email_error}` : '') +
              '. Please check the email configuration or resend later.',
            'warning'
          );
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save user');
      }
    } catch (err: any) {
      setError(err.message);
      showNotification(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (user: User) => {
    setDeleteConfirmDialog({ open: true, user });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmDialog.user) return;

    const userId = deleteConfirmDialog.user.user_id;
    setDeleteConfirmDialog({ open: false, user: null });

    try {
      setDeletingId(userId);
      const response = await fetch(apiUrl(`/api/users/${userId}`), {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        await fetchUsers();
        showNotification('User deleted successfully!', 'success');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete user');
      }
    } catch (err: any) {
      showNotification(err.message, 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleStatusChange = async (user: User, isActive: boolean) => {
    if (access !== 'write' || user.user_id === currentUser?.user_id) return;
    setUpdatingStatusId(user.user_id);
    try {
      const response = await fetch(apiUrl(`/api/users/${user.user_id}/status`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: isActive })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update user status');
        setUsers(previous => previous.map(item => item.user_id === user.user_id
          ? { ...item, is_active: data.is_active } : item));
        await fetchUsers();
      showNotification(isActive ? 'User activated' : 'User deactivated. Existing records are preserved.');
    } catch (err: any) {
      showNotification(err.message, 'error');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmDialog({ open: false, user: null });
  };

  const getRoleBadgeColor = (role: string): 'error' | 'warning' | 'info' | 'default' => {
    switch (role) {
      case 'global_admin':
        return 'error';
      case 'company_admin':
        return 'warning';
      case 'project_admin':
        return 'info';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'global_admin':
        return 'Global Admin';
      case 'company_admin':
        return 'Company Admin';
      case 'project_admin':
        return 'Project Admin';
      default:
        return 'User';
    }
  };

  const getCompanyName = (companyId: number | null) => {
    if (!companyId) return 'N/A';
    const company = companies.find(c => c.company_id === companyId);
    return company ? company.company_name : 'Unknown';
  };

  // DataGrid columns definition
  const columns: GridColDef[] = [
    {
      field: 'user_email',
      headerName: 'User',
      flex: 1.5,
      minWidth: 200,
      valueGetter: (value, row) => {
        // Return display name for filtering to work on name, not email
        if (row.user_first_name || row.user_last_name) {
          return `${row.user_first_name || ''} ${row.user_last_name || ''}`.trim();
        }
        return row.user_email || 'No Name';
      },
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', flexShrink: 0 }}>
            {(params.row.user_first_name?.[0] || params.row.user_email[0]).toUpperCase()}
          </Avatar>
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
            <Typography variant="body2" fontWeight={600} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {params.row.user_first_name || params.row.user_last_name
                ? `${params.row.user_first_name || ''} ${params.row.user_last_name || ''}`.trim()
                : 'No Name'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {params.row.user_email}
            </Typography>
          </Box>
        </Box>
      )
    },
    {
      field: 'access_groups',
      headerName: 'Groups',
      flex: 2,
      minWidth: 250,
      valueGetter: (value, row) => {
        // Return group names for filtering to work
        if (!row.access_groups || row.access_groups.length === 0) {
          return 'No groups';
        }
        return row.access_groups.map((g: any) => g.group_name).join(', ');
      },
      renderCell: (params: GridRenderCellParams) => {
        const groups = params.row.access_groups || [];

        if (groups.length === 0) {
          return (
            <Box>
              <Chip
                label="No groups"
                size="small"
                variant="outlined"
                sx={{ fontStyle: 'italic', color: 'text.secondary', pointerEvents: 'none' }}
              />
            </Box>
          );
        }

        // Show first 2 groups as chips
        const visibleGroups = groups.slice(0, 2);
        const remainingCount = groups.length - 2;

        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', pointerEvents: 'none' }}>
            {visibleGroups.map((group: any, idx: number) => (
              <Tooltip key={idx} title={group.description || group.group_name} placement="top">
                <Chip
                  label={group.group_name}
                  size="small"
                  color={'default'}  // DISABLED temporarily - group.is_system_default ? 'error' : 'default'
                  variant={'outlined'}  // DISABLED temporarily - group.is_system_default ? 'filled' : 'outlined'
                  sx={{
                    fontSize: '0.75rem',
                    maxWidth: 180,
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }
                  }}
                />
              </Tooltip>
            ))}
            {remainingCount > 0 && (
              <Tooltip
                title={groups.slice(2).map((g: any) => g.group_name).join(', ')}
                placement="top"
              >
                <Chip
                  label={`+${remainingCount}`}
                  size="small"
                  color="primary"
                  sx={{ fontSize: '0.7rem', fontWeight: 600, minWidth: 32 }}
                />
              </Tooltip>
            )}
          </Box>
        );
      }
    },
    // HIDDEN: Role column (CLIENT REQUIREMENT: Hide from table view)
    // {
    //   field: 'highest_role',
    //   headerName: 'Role',
    //   flex: 1,
    //   minWidth: 120,
    //   valueGetter: (value, row) => {
    //     // Return role label for filtering to work
    //     return getRoleLabel(row.highest_role);
    //   },
    //   renderCell: (params: GridRenderCellParams) => (
    //     <Chip
    //       label={getRoleLabel(params.row.highest_role)}
    //       color={getRoleBadgeColor(params.row.highest_role)}
    //       size="small"
    //     />
    //   )
    // },
    // HIDDEN: Primary Company column (CLIENT REQUIREMENT: Hide from table view)
    // {
    //   field: 'primary_company_id',
    //   headerName: 'Primary Company',
    //   flex: 1.3,
    //   minWidth: 180,
    //   valueGetter: (value, row) => {
    //     // Return company name for filtering to work
    //     return getCompanyName(row.primary_company_id);
    //   },
    //   renderCell: (params: GridRenderCellParams) => (
    //     <Typography variant="body2">
    //       {getCompanyName(params.row.primary_company_id)}
    //     </Typography>
    //   )
    // },
    {
      field: 'is_active',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 150,
      valueGetter: (value, row) => {
        // Return status label for filtering to work
        return row.is_active ? 'Active' : 'Inactive';
      },
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ width: '100%' }} onClick={event => event.stopPropagation()}>
          <MuiSelect
            size="small"
            fullWidth
            value={params.row.is_active ? 'active' : 'inactive'}
            SelectDisplayProps={{ 'aria-label': `Status for ${params.row.user_email}` }}
            disabled={access !== 'write' || params.row.user_id === currentUser?.user_id || updatingStatusId !== null}
            onChange={event => handleStatusChange(params.row, event.target.value === 'active')}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </MuiSelect>
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      minWidth: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title="Manage Access">
            <IconButton
              size="small"
              onClick={() => handleOpenAccessDialog(params.row)}
              color="primary"
            >
              <SecurityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit User">
            <IconButton
              size="small"
              onClick={() => handleOpenDialog(params.row)}
              disabled={params.row.user_id === currentUser?.user_id}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete User">
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(params.row)}
              disabled={deletingId === params.row.user_id || params.row.user_id === currentUser?.user_id}
              color="error"
            >
              {deletingId === params.row.user_id ? (
                <CircularProgress size={20} />
              ) : (
                <DeleteIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  if (access === 'none') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You don't have permission to manage users.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3, flexShrink: 0 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add User
        </Button>
      </Box>

      {/* Search */}
      <Alert severity="info" sx={{ mb: 2, flexShrink: 0 }}>
        Inactive users cannot sign in. Their records and documents remain available to authorized users.
      </Alert>
      <Box sx={{ mb: 3, flexShrink: 0, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          exclusive
          value={statusFilter}
          size="small"
          aria-label="Filter users by status"
          disabled={updatingStatusId !== null}
          onChange={(_, value) => {
            if (value) {
              setStatusFilter(value);
              setPage(1);
            }
          }}
          sx={{
            p: 0.4, bgcolor: '#f1f5f9', border: '1px solid', borderColor: 'divider', borderRadius: 2,
            '& .MuiToggleButtonGroup-grouped': {
              border: 0, borderRadius: '6px !important', textTransform: 'none', px: 2, py: 0.7,
              color: 'text.secondary',
              '&.Mui-selected': {
                bgcolor: 'background.paper', color: 'primary.main', fontWeight: 600,
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                '&:hover': { bgcolor: 'background.paper' }
              }
            }
          }}
        >
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="inactive">Inactive</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          fullWidth
          sx={{ flex: 1, minWidth: 240 }}
          placeholder="Search users by name or email..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1); // Reset to first page when searching
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Users DataGrid */}
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <StyledDataGrid
          rows={users}
          columns={columns}
          getRowId={(row) => row.user_id}
          loading={loading}
          totalRows={totalUsers}
          page={page}
          pageSize={perPage}
          onPageChange={setPage}
          onPageSizeChange={setPerPage}
          onRowDoubleClick={(params) => {
            if (params.row.user_id !== currentUser?.user_id) {
              handleOpenDialog(params.row);
            }
          }}
        />
      </Box>

      {/* User Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <TextField
              label="First Name"
              fullWidth
              value={formData.user_first_name}
              onChange={(e) => handleInputChange('user_first_name', e.target.value)}
            />

            <TextField
              label="Last Name"
              fullWidth
              value={formData.user_last_name}
              onChange={(e) => handleInputChange('user_last_name', e.target.value)}
            />

            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={formData.user_email}
              onChange={(e) => handleInputChange('user_email', e.target.value)}
              disabled={!!editingUser}
            />

            {/* New users set their own password via the emailed setup link,
                so the admin does not enter a password at creation time. The
                password field only appears when editing an existing user
                (optional admin reset). */}
            {editingUser ? (
              <>
                <TextField
                  label="New Password (leave blank to keep current)"
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  error={formData.password.length > 0 && !validatePassword(formData.password).isValid}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                {/* For edits the field is optional, so the checklist is hidden
                    until the admin starts typing a new password. */}
                {formData.password.length > 0 && (
                  <PasswordPolicyChecklist password={formData.password} />
                )}
              </>
            ) : (
              <Alert severity="info" sx={{ mt: 1 }}>
                A "Set Up My Account" link will be emailed to this user. They
                set their own password and authenticator from that link — no
                password is created here.
              </Alert>
            )}

            {!editingUser && (
              <>
                {/* Groups Selection - CLIENT REQUIREMENT: Group-based access control, MANDATORY */}
                <Typography variant="subtitle2" sx={{ mt: 2, color: 'error.main' }}>
                  Access Groups (Required) *
                </Typography>

                <Autocomplete
                  multiple
                  fullWidth
                  options={groups}
                  getOptionLabel={(option) => option.group_name}
                  value={groups.filter(g => formData.group_ids.includes(g.group_id))}
                  onChange={(e, newValue) => {
                    handleInputChange('group_ids', newValue.map(g => g.group_id));
                  }}
                  isOptionEqualToValue={(option, value) => option.group_id === value.group_id}
                  ChipProps={{
                    onDelete: undefined,
                    deleteIcon: undefined
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Groups (Required)"
                      placeholder="Type to search groups..."
                      required
                      error={formData.group_ids.length === 0}
                      helperText={formData.group_ids.length === 0 ? "⚠️ At least one group is REQUIRED. Groups must be created manually first." : "Type to search groups by name"}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option.group_id}
                        label={option.group_name}
                        size="small"
                        onDelete={(e) => {
                          e.stopPropagation();
                          const newGroups = formData.group_ids.filter(id => id !== option.group_id);
                          handleInputChange('group_ids', newGroups);
                        }}
                      />
                    ))
                  }
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {option.group_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description || 'No description'}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || (!editingUser && formData.group_ids.length === 0)}
          >
            {saving ? <CircularProgress size={24} /> : editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Access Dialog */}
      <Dialog
        open={openAccessDialog}
        onClose={handleCloseAccessDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Manage User Access - {managingUser?.user_email}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Select groups to assign access to this user:
            </Typography>

            {groups.length === 0 ? (
              <Alert severity="info">No groups available</Alert>
            ) : (
              <>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search groups by name or description..."
                  value={groupSearchTerm}
                  onChange={(e) => setGroupSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: groupSearchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setGroupSearchTerm('')}
                          edge="end"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{ mb: 1 }}
                />
                <Box sx={{ maxHeight: 400, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                  {groups
                    .filter((group) => {
                      const searchLower = groupSearchTerm.toLowerCase();
                      return (
                        group.group_name.toLowerCase().includes(searchLower) ||
                        (group.description && group.description.toLowerCase().includes(searchLower))
                      );
                    })
                    .map((group) => (
                  <Box
                    key={group.group_id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 1,
                      '&:hover': { bgcolor: 'action.hover' },
                      borderRadius: 1
                    }}
                  >
                    <Checkbox
                      checked={accessFormData.group_ids.includes(group.group_id)}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        setAccessFormData(prev => ({
                          group_ids: isChecked
                            ? [...prev.group_ids, group.group_id]
                            : prev.group_ids.filter(id => id !== group.group_id)
                        }));
                      }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {group.group_name}
                      </Typography>
                      {group.description && (
                        <Typography variant="caption" color="text.secondary">
                          {group.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
                  {groups.filter((group) => {
                    const searchLower = groupSearchTerm.toLowerCase();
                    return (
                      group.group_name.toLowerCase().includes(searchLower) ||
                      (group.description && group.description.toLowerCase().includes(searchLower))
                    );
                  }).length === 0 && (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No groups found matching "{groupSearchTerm}"
                      </Typography>
                    </Box>
                  )}
              </Box>
              </>
            )}

            <Typography variant="caption" color="text.secondary">
              💡 User will inherit permissions from all selected groups.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseAccessDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveAccess}
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main', fontWeight: 600 }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user{' '}
            <strong>
              {deleteConfirmDialog.user?.user_first_name || deleteConfirmDialog.user?.user_last_name
                ? `${deleteConfirmDialog.user?.user_first_name || ''} ${deleteConfirmDialog.user?.user_last_name || ''}`.trim()
                : deleteConfirmDialog.user?.user_email}
            </strong>
            ?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleDeleteCancel} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Users;
